import {TableIcon} from '@primer/octicons-react'
import {Fragment} from 'react'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import styles from './AddedToProjectV2Event.module.css'
import type {AddedToProjectV2EventNode} from './issue-viewer-page-query-types'
import {ProjectV2} from './ProjectV2'
import {RolledupProjectV2Event} from './RolledupProjectV2Event'
import {TimelineRow} from './row/TimelineRow'

export type AddedToProjectV2EventFragment = Pick<
  AddedToProjectV2EventNode,
  'actor' | 'createdAt' | 'project' | 'databaseId'
>

type AddedToProjectV2EventProps = {
  queryRef: AddedToProjectV2EventFragment
  issueUrl: string
  rollupGroup?: Record<string, AddedToProjectV2EventFragment[]>
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
}

export function AddedToProjectV2Event({
  queryRef,
  issueUrl,
  rollupGroup,
  highlightedEventId,
  refAttribute,
}: AddedToProjectV2EventProps): React.ReactElement {
  const {actor, createdAt, project, databaseId} = queryRef

  if (!project) {
    return <></>
  }

  const hightlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={hightlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      leadingIcon={TableIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupProjectV2Event rollupGroup={rollupGroup} />
        ) : (
          <AddedToProjectV2sRendering queryRefs={[queryRef]} />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const AddedToProjectV2sRendering = ({queryRefs}: {queryRefs: AddedToProjectV2EventFragment[]}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {`${LABELS.timeline.addedThisTo} `}
      {queryRefs.map((queryRef, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <InternalAddedToProjectV2sRendering
            queryRef={queryRef}
            first={index === 0}
            last={index === queryRefs.length - 1}
          />
        </Fragment>
      ))}
    </>
  )
}

const InternalAddedToProjectV2sRendering = ({
  queryRef,
  first,
  last,
}: {
  queryRef: AddedToProjectV2EventFragment
  first: boolean
  last: boolean
}) => {
  const {project} = queryRef

  if (!project?.title || !project?.url) {
    return null
  }

  return (
    <span className={styles.projectNameWrapper}>
      <>
        {!first && !last && <span className={styles.commaSeparator}>,</span>}
        {!first && last && <span className={styles.andSeparator}>{LABELS.timeline.and}</span>}
        <ProjectV2 title={project?.title} url={project?.url} />
      </>
    </span>
  )
}
